import { startRegistration, startAuthentication, platformAuthenticatorIsAvailable } from '@simplewebauthn/browser';
import { api } from '../api';

// ─── Passkeys: Face ID / fingerprint sign-in ─────────────────────────────────
//
// The browser does the interesting part. We fetch a challenge, hand it to the
// platform authenticator, and post back what it signs. No biometric data
// passes through here — the phone checks the face or finger itself and only
// releases a signature.

/**
 * Can this device offer a built-in biometric prompt?
 *
 * Checked before showing anything, because offering "Sign in with Face ID" on
 * a machine that has no such thing and then failing is worse than not
 * offering it. Older browsers lack the API entirely, hence the guard.
 */
export async function passkeysAvailable(): Promise<boolean> {
  try {
    if (typeof window.PublicKeyCredential === 'undefined') return false;
    return await platformAuthenticatorIsAvailable();
  } catch {
    return false;
  }
}

/** A guess at what to call this device, so a lost phone is recognisable in the list. */
export function deviceLabel(): string {
  const ua = navigator.userAgent;
  if (/iphone/i.test(ua)) return 'iPhone';
  if (/ipad/i.test(ua)) return 'iPad';
  if (/android/i.test(ua)) return 'Android phone';
  if (/mac/i.test(ua)) return 'Mac';
  if (/windows/i.test(ua)) return 'Windows PC';
  return 'This device';
}

/** Enrol the current device. Requires an existing session. */
export async function enrolPasskey(label?: string): Promise<void> {
  const { data } = await api.post('/auth/passkey/register/options');
  const response = await startRegistration({ optionsJSON: data.options });
  await api.post('/auth/passkey/register/verify', {
    handle: data.handle,
    response,
    label: label ?? deviceLabel(),
  });
}

/**
 * Sign in with a passkey. Returns the session payload, shaped exactly like
 * the password and code endpoints return.
 */
export async function signInWithPasskey(): Promise<any> {
  const { data } = await api.post('/auth/passkey/auth/options');
  const response = await startAuthentication({ optionsJSON: data.options });
  const verified = await api.post('/auth/passkey/auth/verify', {
    handle: data.handle,
    response,
  });
  return verified.data;
}

/**
 * True when the user dismissed the system prompt rather than hitting a real
 * failure. Cancelling is not an error worth shouting about.
 */
export function wasCancelled(err: any): boolean {
  const name = err?.name || err?.cause?.name;
  return name === 'NotAllowedError' || name === 'AbortError';
}

export interface EnrolledPasskey {
  id: number;
  label: string | null;
  createdAt: string;
  lastUsedAt: string | null;
}

export const listPasskeys = () => api.get<EnrolledPasskey[]>('/auth/passkey/credentials');
export const removePasskey = (id: number) => api.delete(`/auth/passkey/credentials/${id}`);
